import { useCallback, useEffect, useRef, useState } from 'react';

import { ReloadFailedError, RELOAD_FAILED_MESSAGE } from './useJourneys';

import { JourneyChanges, lifecycleErrorMessage } from '@/services/lifecycleAPI';

type Source = 'row' | 'detail';

const useRowSave = (
  journeyKey: string,
  onUpdate: (key: string, changes: JourneyChanges) => Promise<void>,
) => {
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<{
    source: Source;
    message: string;
  } | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (changes: JourneyChanges, source: Source) => {
      setPending(true);
      setFailure(null);
      let ok = true;
      try {
        await onUpdate(journeyKey, changes);
      } catch (err) {
        if (err instanceof ReloadFailedError) {
          if (mounted.current) {
            setFailure({ source, message: RELOAD_FAILED_MESSAGE });
          }
        } else {
          ok = false;
          if (mounted.current) {
            setFailure({
              source,
              message: lifecycleErrorMessage(err, 'Could not save the change.'),
            });
          }
        }
      } finally {
        if (mounted.current) setPending(false);
      }
      return ok;
    },
    [journeyKey, onUpdate],
  );

  const errorFor = (source: Source) =>
    failure?.source === source ? failure.message : null;

  return { pending, run, errorFor };
};

export default useRowSave;
